import { User } from '@supabase/supabase-js';
import { NestedHome, NestedSeller, NestedUser } from '@habity/types';
import { TODO } from '@habity/types';
import { getUserAvatarUrl } from '~/lib/providers/supabase/avatars';
import { cache } from 'react';
import { getUserDataQuery } from './query';

/**
 * Default user data fetch for layout/context - fetches fields needed for:
 * - AppProvider context (basic user info)
 * - Tracking identification (Customer.io, PostHog, etc.)
 * - Role checks (isBrokerOrAdmin)
 * - Menu navigation (homes count, searches count, wishlists count)
 *
 * Fetches minimal data for wishlists/searches (just IDs for counting).
 * For pages that need full user data with homes, use getFullUserData instead.
 *
 * Note: The user_roles join can be removed for better performance once custom_access_token_hook
 * is confirmed working. Roles are also available via JWT claims, but this join provides
 * real-time role updates without requiring re-login.
 *
 * Query columns are defined in ./query.ts - SINGLE SOURCE OF TRUTH shared with client-side SWR
 */
export const getUserData = cache(async (
  supabase: TODO,
  authUser: User | null
): Promise<NestedUser | null> => {
  if (!authUser) return null;

  const { data: userData, error: userError } = await supabase
    .from('users')
    .select(getUserDataQuery())
    .or(`uuid.eq.${authUser.id},email.eq.${authUser.email}`)
    .limit(1)
    .maybeSingle();

  if (userError) {
    console.error('[getUserData] error', userError.message);
    return null;
  }

  if (!userData) return null;

  // Join avatar
  const avatarUrl = userData.user_images?.[0]
    ? getUserAvatarUrl(
      supabase,
      'user-images',
      userData.id,
      userData.user_images[0].extension || ''
    )
    : undefined;

  // Get homes count via sellers table (lightweight - just unique IDs)
  // Use distinct home_id since sellers table now has multiple events per user-home pair
  const { data: sellerRecords } = await supabase
    .from('sellers')
    .select('home_id')
    .eq('user_id', userData.id);

  // Deduplicate home_ids since a user can have multiple events for the same home
  const uniqueHomeIds = [...new Set(sellerRecords?.map((s: { home_id: number }) => s.home_id) || [])];
  const homes = uniqueHomeIds.map((id) => ({ id }));

  // Extract roles from joined user_roles
  const roles = userData.user_roles?.map((r: { role: string }) => r.role) || [];

  return {
    ...userData,
    avatar_url: avatarUrl || userData.avatar_url,
    homes,
    homes_count: homes.length,
    roles,
  };
});

/**
 * Full user data fetch including homes - use for pages that need seller home data.
 * Wrapped with React cache() to deduplicate calls within the same request.
 *
 * Note: The user_roles join can be removed for better performance once custom_access_token_hook
 * is confirmed working. Roles are also available via JWT claims, but this join provides
 * real-time role updates without requiring re-login.
 */
export const getFullUserData = cache(async (
  supabase: TODO,
  authUser: User | null
): Promise<NestedUser | null> => {
  if (!authUser) return null;

  const { data: userData, error: userError } = await supabase
    .from('users')
    .select(
      `
      id,
      uuid,
      email,
      phone,
      first_name,
      last_name,
      avatar_url,
      role,
      app_mode,
      created_at,
      updated_at,
      id_verified_at,
      terms_accepted_at,
      privacy_policy_accepted_at,
      provider,
      provider_id,
      loan_promise,
      loan_promise_expires_at,
      loan_contact_accepted_at,
      loan_contact_denied_at,
      source,
      address_street,
      address_zip,
      address_city,
      address_country,
      broker_id,
      broker:broker_id(
        id,
        name,
        email,
        phone,
        title
      ),
      user_images(
        id,
        extension
      ),
      searches(id),
      interests(id, home_id, interest_level),
      user_roles(role)
    `
    )
    .or(`uuid.eq.${authUser.id},email.eq.${authUser.email}`)
    .limit(1)
    .maybeSingle();

  if (userError) {
    console.error('[getFullUserData] error', userError.message);
    return null;
  }

  if (!userData) return null;

  // Join avatar
  const avatarUrl = userData.user_images?.[0]
    ? getUserAvatarUrl(
      supabase,
      'user-images',
      userData.id,
      userData.user_images[0].extension || ''
    )
    : undefined;

  // Join homes
  // Claimed homes + valuations
  const { data: sellerRecords }: { data: NestedSeller[] } = await supabase
    .from('sellers')
    .select('*')
    .eq('user_id', userData.id);

  // Deduplicate home_ids since a user can have multiple events for the same home
  const homeIds = [...new Set(sellerRecords?.map((seller: any) => seller.home_id) || [])];

  let homes: any[] = [];

  if (homeIds.length > 0) {
    const { data: homesData, error: homesError } = await supabase
      .from('homes')
      .select(
        `
            *,
            address:addresses!homes_address_id_fkey(*),
            status:status_id(*),
            images!images_home_id_fkey(*),
            responsible_broker:responsible_broker_id(*),
            additional_contact:additional_contact_id(*),
            valuations:valuations_home_id_fkey1(*)
          `
      )
      .in('id', homeIds);

    if (homesError) console.error('[getFullUserData] homesError:', homesError);
    homes = homesData || [];
  }

  // Extract roles from joined user_roles
  const roles = userData.user_roles?.map((r: { role: string }) => r.role) || [];

  return {
    ...userData,
    avatar_url: avatarUrl || userData.avatar_url,
    homes,
    roles,
    seller_records: sellerRecords,
  };
});

/**
 * Seller-specific data fetch - gets user with homes count but not full homes.
 * Wrapped with React cache() to deduplicate calls within the same request.
 *
 * Note: The user_roles join can be removed for better performance once custom_access_token_hook
 * is confirmed working. Roles are also available via JWT claims, but this join provides
 * real-time role updates without requiring re-login.
 */
export const getSellerData = cache(async (
  supabase: TODO,
  authUser: User | null
): Promise<NestedUser | null> => {
  let user: NestedUser | null = null;

  if (authUser) {
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select(
        `
        id,
        uuid,
        email,
        phone,
        first_name,
        last_name,
        avatar_url,
        role,
        app_mode,
        created_at,
        updated_at,
        id_verified_at,
        terms_accepted_at,
        privacy_policy_accepted_at,
        provider,
        provider_id,
        loan_promise,
        loan_promise_expires_at,
        source,
        address_street,
        address_zip,
        address_city,
        address_country,
        broker_id,
        broker:broker_id(
          id,
          name,
          email,
          phone,
          title
        ),
        user_images(
          id,
          extension
        ),
        user_roles(role)
      `
      )
      .or(`uuid.eq.${authUser.id},email.eq.${authUser.email}`)
      .limit(1)
      .maybeSingle();

    if (userError) console.log('error', userError.message);
    user = userData || null;

    if (user) {
      // Join avatar
      const avatarUrl = userData.user_images?.[0]
        ? getUserAvatarUrl(
          supabase,
          'user-images',
          userData.id,
          userData.user_images[0].extension || ''
        )
        : undefined;

      user = {
        ...userData,
        avatar_url: avatarUrl || user.avatar_url,
      };

      // Find home count, don't join the homes because we will fetch them
      // separately from neondb for a fast optimized query
      const { data: sellerRecords } = await supabase
        .from('sellers')
        .select('home_id')
        .eq('user_id', userData.id);

      // Deduplicate home_ids since a user can have multiple events for the same home
      const uniqueHomeIds = [...new Set(sellerRecords?.map((s: { home_id: number }) => s.home_id) || [])];
      const homes_count = uniqueHomeIds.length;

      // Extract roles from joined user_roles
      const roles = userData.user_roles?.map((r: { role: string }) => r.role) || [];

      user = {
        ...userData,
        homes_count,
        roles,
        // Initialize empty arrays for optional relations
        interests: [],
        searches: [],
        homes: [],
      };
    }
  }

  return user;
});

export async function getSellerHomeIds(
  supabase: TODO,
  authUser: User
): Promise<number[]> {
  const { data: userData } = await supabase
    .from('users')
    .select('id')
    .or(`uuid.eq.${authUser.id},email.eq.${authUser.email}`)
    .limit(1)
    .maybeSingle();

  if (!userData) return [];

  const { data: sellerRecords } = await supabase
    .from('sellers')
    .select('home_id')
    .eq('user_id', userData.id);

  // Deduplicate home_ids since a user can have multiple events for the same home
  const homeIds = [...new Set(sellerRecords?.map((seller: any) => seller.home_id as number) || [])] as number[];
  return homeIds;
}

// FIXME: This is VERY VERY slow
export async function getSellerHomeById(
  supabase: TODO,
  authUser: User,
  homeId: number
): Promise<NestedHome | null> {
  const { data: userData } = await supabase
    .from('users')
    .select('id')
    .or(`uuid.eq.${authUser.id},email.eq.${authUser.email}`)
    .limit(1)
    .maybeSingle();

  if (!userData) return null;

  const { data: sellerRecord, error: sellerError } = await supabase
    .from('sellers')
    .select('home_id')
    .eq('user_id', userData.id)
    .eq('home_id', homeId)
    .limit(1)
    .maybeSingle();

  if (sellerError) {
    console.error('Error fetching seller record:', sellerError.message);
    return null;
  }

  if (!sellerRecord) {
    return null;
  }

  const { data: homeData, error: homeError } = await supabase
    .from('homes')
    .select(
      `
      *,
      address:addresses!homes_address_id_fkey(*),
      status:status_id(*),
      images!images_home_id_fkey(*),
      responsible_broker:responsible_broker_id(*),
      additional_contact:additional_contact_id(*)
    `
    )
    .eq('id', homeId)
    .maybeSingle();

  if (homeError) {
    console.error('Error fetching home data:', homeError.message);
    return null;
  }

  return homeData ?? null;
}
